import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {useAnonymousAuth} from '@src/Providers/AnonymousAuthProvider/AnonymousAuthProvider';
import {useAppSelector, useAppDispatch} from '@src/Store/hooks';
import ModalAuthentication from '@src/App/components/ModalAuthentication/ModalAuthentication';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import {
  contentAccessService,
  ContentAccessState,
} from '@src/Services/ContentAccess/ContentAccessService';
import {getAppRoutePrefix} from '@src/utils/navigationUtils';
import {Routes} from '@src/Constant/routes';
import {navigate} from 'gatsby';
import {ensureCleanLocalePrefix} from '@src/utils/url/ensureCleanLocalePrefix';
import {useI18next} from 'gatsby-plugin-react-i18next';

type ModalType = 'auth' | 'paywall' | 'none';

interface ModalDisplay {
  type: ModalType;
  visible: boolean;
  reason?: string;
}

/**
 * ModalManager centralisé - Inspiré de la PR #1
 * Gère l'affichage de toutes les modales d'authentification
 *
 * Priorités:
 * 1. Paywall (utilisateur connecté sans premium)
 * 2. Auth (utilisateur non connecté)
 * 3. Aucune modale
 */
export const ModalManager: React.FC = () => {
  const {isAnonymous, user} = useAnonymousAuth();
  const {language} = useI18next();
  const dispatch = useAppDispatch();

  // États Redux pour les modales (ancien système)
  const openAuthenticationModal = useAppSelector(
    state => state.appAuth.openAuthenticationModal,
  );

  // État du ContentAccessService (nouveau système)
  const [contentAccessState, setContentAccessState] = useState(
    contentAccessService.getCurrentState(),
  );

  const [currentModal, setCurrentModal] = useState<ModalDisplay>({
    type: 'none',
    visible: false,
  });

  const cleanRedirectUrl = useMemo(() => {
    const input =
      contentAccessState.currentContentData?.redirectTo || Routes.APP;
    return ensureCleanLocalePrefix(input);
  }, [contentAccessState.currentContentData?.redirectTo, language]);

  /**
   * Redirige vers la home page
   */
  const handleSafeNavigationBack = useCallback(() => {
    const appRoutePrefix = getAppRoutePrefix();
    const homeRoute = `${appRoutePrefix}${Routes.HOME}`;
    const safeHomeRoute = ensureCleanLocalePrefix(homeRoute);

    console.log('🧠 [ModalManager] Redirecting to home ...');

    navigate(safeHomeRoute, {
      replace: true,
      state: {
        fromAuthModal: true,
      },
    });
  }, [language]);

  // Handlers pour fermer les modales
  const handleCloseAuthModal = useCallback(() => {
    console.log('🎭 [ModalManager] Closing auth modal');

    // 🎯 Dispatcher l'action Redux pour fermer la modal (gérée par le middleware)
    dispatch(appAuthSlice.actions.closeAuthModal());

    // 🧠 Navigation sécurisée qui évite les boucles infinies d'auth
    handleSafeNavigationBack();
  }, [dispatch, handleSafeNavigationBack]);

  // Écouter les changements du ContentAccessService
  useEffect(() => {
    const subscription = contentAccessService.state.subscribe(
      (newState: ContentAccessState) => {
        setContentAccessState(newState);
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  // Logique de détermination du modal à afficher
  useEffect(() => {
    // Déterminer le type de modal à afficher selon la priorité
    let modalToShow: ModalDisplay = {type: 'none', visible: false};

    // 🎯 PRIORITÉ 1: ContentAccessService (nouveau système)
    if (contentAccessState.showAuthModal) {
      modalToShow = {
        type: 'auth',
        visible: true,
        reason: 'Content access authentication required',
      };
    }
    // 🎯 PRIORITÉ 2: Paywall (futur)
    else if (contentAccessState.showPaywall) {
      modalToShow = {
        type: 'paywall',
        visible: true,
        reason: 'Premium subscription required',
      };
    }
    // 🎯 PRIORITÉ 3: Ancien système Redux (fallback)
    else if (openAuthenticationModal?.open) {
      modalToShow = {
        type: 'auth',
        visible: true,
        reason: 'Legacy authentication required',
      };
    }

    if (modalToShow.type !== 'none') {
      console.log('🎭 [ModalManager] Evaluating modal display state', {
        isAnonymous,
        hasUser: !!user,
        openAuthModal: openAuthenticationModal?.open,
        contentAccessShowAuth: contentAccessState.showAuthModal,
        contentAccessShowPaywall: contentAccessState.showPaywall,
      });
    }

    // Mettre à jour l'état du modal
    setCurrentModal(modalToShow);

    console.log('🎭 [ModalManager] Modal decision:', modalToShow);
  }, [isAnonymous, user, openAuthenticationModal, contentAccessState]);

  // Rendu conditionnel des modales
  return (
    <>
      {/* Modal d'authentification */}
      {currentModal.type === 'auth' && (
        <ModalAuthentication
          isOpen={currentModal.visible}
          onClose={handleCloseAuthModal}
          redirect={cleanRedirectUrl}
        />
      )}

      {/* Futur: Modal Paywall */}
      {currentModal.type === 'paywall' && (
        <div>
          {/* PaywallModal sera ajouté ici plus tard */}
          <p>Paywall Modal (à implémenter)</p>
        </div>
      )}

      {/* Gestion du scroll pour empêcher le défilement quand modal ouverte */}
      <style
        dangerouslySetInnerHTML={{
          __html: `body {overflow: ${currentModal.visible ? 'hidden' : 'auto!important'};}`,
        }}
      />
    </>
  );
};

export default ModalManager;
